// src/components/common/Header/HeaderSearch.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  Eye,
  Edit,
  ExternalLink,
  ShoppingCart,
  User,
  MapPin,
  Package,
  Loader,
  Phone,
  Tag
} from 'lucide-react';
import { getEcomOrders } from '../../../services/api/orders';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency } from '../../../utils/orderCalculations';
import { isOfflineChannel } from '../../../config/app.config';
import styles from './HeaderSearch.module.css';

/**
 * Normalizes a search query for phone number lookups.
 * If the query looks like a local phone number starting with 0 (e.g. 0771234567),
 * the leading 0 is stripped so it matches the +94XXXXXXXXX format stored in the DB.
 */
const normalizeSearchQuery = (query) => {
  if (!query) return query;
  const trimmed = query.trim();
  // Match a local phone number: starts with 0, followed by 9 digits (greater than 3 and less than or equal 10)
  if (/^0\d{3,9}$/.test(trimmed)) {
    return trimmed.slice(1); // strip leading 0 → "771234567" matches "+94771234567"
  }
  return trimmed;
};

const HeaderSearch = ({ onSearchStart, onSearchEnd }) => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);
  
  const searchRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Reset results if query is too short
    if (searchQuery.length < 5) {
      setSearchResults([]);
      setShowResults(false);
      setError(null);
      if (onSearchEnd) onSearchEnd();
      return;
    }

    // Start searching after 500ms delay
    debounceTimerRef.current = setTimeout(() => {
      performSearch(searchQuery);
    }, 500);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const performSearch = async (query) => {
    if (!query || query.length < 5) return;

    setIsSearching(true);
    setError(null);
    if (onSearchStart) onSearchStart();

    try {
      const response = await getEcomOrders({
        search: normalizeSearchQuery(query),
        limit: 10,
        page: 1,
        orderBy: 'createdAt',
        orderDirection: 'DESC',
        includeOfflineChannel: true
      });

      setSearchResults(response.orders || []);
      setShowResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setError(t('header.searchError') || 'Failed to search orders');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
      if (onSearchEnd) onSearchEnd();
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    setError(null);
  };

  const handleViewOrder = (order) => {
    const path = isOfflineChannel(order.channel) ? `/offline-sales/orders/${order.id}` : `/orders/${order.id}`;
    window.open(path, '_blank');
    handleClearSearch();
  };

  const handleEditOrder = (order, e) => {
    e.stopPropagation();
    const path = isOfflineChannel(order.channel) ? `/offline-sales/orders/${order.id}/edit` : `/orders/${order.id}/edit`;
    window.open(path, '_blank');
    handleClearSearch();
  };

  const handleViewAllResults = () => {
    navigate(`/orders?search=${encodeURIComponent(searchQuery)}`);
    handleClearSearch();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/orders?search=${encodeURIComponent(searchQuery)}`);
      handleClearSearch();
    }
  };

  const getOrderStatusColor = (order) => {
    return order.OrderStatus?.colorCode || '#F59E0B';
  };

  const canEditOrder = (order) => {
    if (isOfflineChannel(order.channel)) {
      return !order.OrderStatus?.isClosed && hasPermission('edit_offline_sales_order');
    }

    if (order.OrderStatus?.isInPacking && !hasPermission('edit_in_packing_order')) {
      return false;
    }

    if (order.OrderStatus?.isDispatched && !hasPermission('edit_dispatched_order')) {
      return false;
    }

    return hasPermission('edit_ecom_order');
  };

  return (
    <div className={styles.headerSearch} ref={searchRef}>
      <form onSubmit={handleSubmit} className={styles.searchForm}>
        <div className={styles.searchInputWrapper}>
          <Search 
            size={18} 
            className={`${styles.searchIcon} ${isSearching ? styles.searching : ''}`} 
          />
          {isSearching && (
            <Loader size={16} className={styles.loadingSpinner} />
          )}
          <input
            type="text"
            placeholder={t('header.searchPlaceholder')}
            value={searchQuery}
            onChange={handleSearchChange}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className={styles.clearButton}
              title={t('common.clear')}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </form>

      {/* Search Results Dropdown */}
      {showResults && searchQuery.length >= 5 && (
        <div className={styles.searchResults}>
          {/* Header */}
          <div className={styles.resultsHeader}>
            <h3>
              {t('header.searchResults')} 
              {searchResults.length > 0 && ` (${searchResults.length})`}
            </h3>
          </div>

          {/* Loading State */}
          {isSearching && (
            <div className={styles.loadingState}>
              <Loader size={24} className={styles.loadingSpinnerLarge} />
              <p>{t('header.searching')}</p>
            </div>
          )}

          {/* Error State */}
          {error && !isSearching && (
            <div className={styles.errorState}>
              <p className={styles.errorMessage}>{error}</p>
            </div>
          )}

          {/* Results List */}
          {!isSearching && !error && searchResults.length > 0 && (
            <div className={styles.resultsList}>
              {searchResults.map((order) => (
                <div
                  key={order.id}
                  className={styles.resultItem}
                  onClick={() => handleViewOrder(order)}
                >
                  {/* Order Header */}
                  <div className={styles.resultHeader}>
                    <div className={styles.orderNumber}>
                      <ShoppingCart size={14} />
                      #{order.orderNumber}
                    </div>
                    <div className={styles.statusBadges}>
                      <div
                        className={styles.orderStatus}
                        style={{
                          backgroundColor: `${getOrderStatusColor(order)}20`,
                          borderColor: getOrderStatusColor(order),
                          color: getOrderStatusColor(order)
                        }}
                      >
                        {order.OrderStatus?.label || order.orderStatusKey}
                      </div>
                      {order.courierStatus?.statusLabel && (
                        <div className={styles.courierStatus}>
                          {order.courierStatus.statusLabel}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className={styles.customerInfo}>
                    <div className={styles.customerName}>
                      <User size={12} />
                      {order.customerName}
                    </div>
                    <div className={styles.customerCity}>
                      <MapPin size={12} />
                      {order.City?.name || 'N/A'}
                    </div>
                  </div>

                  {/* Airwaybill & Phone Numbers */}
                  {(order.airwaybillNumber || (order.phoneNumbers && order.phoneNumbers.length > 0)) && (
                    <div className={styles.orderMeta}>
                      {order.airwaybillNumber && (
                        <div className={styles.airwaybill}>
                          <Tag size={12} />
                          {order.airwaybillNumber}
                        </div>
                      )}
                      {order.phoneNumbers && order.phoneNumbers.length > 0 && (
                        <div className={styles.phoneNumbers}>
                          <Phone size={12} />
                          {order.phoneNumbers.map((p, idx) => (
                            <span key={idx} className={p.isPrimary ? styles.primaryPhone : styles.secondaryPhone}>
                              {p.phoneNumber}{idx < order.phoneNumbers.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Products */}
                  {order.orderItems && order.orderItems.length > 0 && (
                    <div className={styles.products}>
                      <Package size={12} />
                      <span className={styles.productsText}>
                        {order.orderItems.slice(0, 2).map((item, idx) => (
                          <span key={idx}>
                            {item.Product?.name || 'N/A'} × {item.quantity}
                            {idx < Math.min(order.orderItems.length - 1, 1) && ', '}
                          </span>
                        ))}
                        {order.orderItems.length > 2 && ` +${order.orderItems.length - 2} more`}
                      </span>
                    </div>
                  )}

                  {/* Order Value & Actions */}
                  <div className={styles.resultFooter}>
                    <div className={styles.orderValue}>
                      {formatCurrency(order.grandTotal)}
                    </div>
                    <div className={styles.resultActions}>
                      {(isOfflineChannel(order.channel) ? hasPermission('view_offline_sales_orders') : hasPermission('view_ecom_order')) && (
                        <button
                          className={`${styles.actionButton} ${styles.viewButton}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrder(order);
                          }}
                          title={t('common.view')}
                        >
                          <Eye size={14} />
                        </button>
                      )}
                      {canEditOrder(order) && (
                        <button
                          className={`${styles.actionButton} ${styles.editButton}`}
                          onClick={(e) => handleEditOrder(order, e)}
                          title={t('common.edit')}
                        >
                          <Edit size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {!isSearching && !error && searchResults.length === 0 && (
            <div className={styles.noResults}>
              <ShoppingCart size={32} className={styles.noResultsIcon} />
              <p className={styles.noResultsText}>
                {t('header.noOrdersFound')}
              </p>
              <p className={styles.noResultsHint}>
                {t('header.searchHint')}
              </p>
            </div>
          )}

          {/* Footer */}
          {!isSearching && searchResults.length > 0 && (
            <div className={styles.resultsFooter}>
              <button 
                onClick={handleViewAllResults}
                className={styles.viewAllButton}
              >
                <ExternalLink size={14} />
                {t('header.viewAllResults')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;